import { useEffect, useState } from 'react';
import { t } from '@lingui/macro';
import { MenuItem } from '@material-ui/core';
import { has, isEmpty, isNil } from 'lodash';
import { useFormContext } from 'react-hook-form';

import NgpSelect from '../../form/NgpSelect';
import { usePlmMainMenuPages } from '../../side-nav/menus/PlmMainMenuPages';
import {
    DEFAULT_GROUP_UUID,
    GROUPS_ADMIN_DASHBOARDS_FIELD,
    GROUPS_ADMIN_LANDING_PAGE_FIELD,
    GROUPS_ADMIN_PAGES_FIELD,
    NO_LANDING_PAGE_DEFAULT,
} from './GroupsAdminConstants';
import { useGroupAdminStyles } from './GroupsAdminUtils';
import { useEntityIdParam } from '../AdminLinkUtils';

export function GroupsAdminLandingPageSelect({ formData, disabled, dashboards }) {
    const recordId = useEntityIdParam();
    const [menuItems, setMenuItems] = useState([]);
    const { setValue, watch } = useFormContext();
    const styles = useGroupAdminStyles();
    const plmMainMenuPages = usePlmMainMenuPages();
    const formPages = watch(GROUPS_ADMIN_PAGES_FIELD);
    const formDashboards = watch(GROUPS_ADMIN_DASHBOARDS_FIELD);
    const formLandingPage = watch(GROUPS_ADMIN_LANDING_PAGE_FIELD);

    useEffect(() => {
        const availablePages = isNil(formPages) ? formData?.[GROUPS_ADMIN_PAGES_FIELD] || [] : formPages;
        let availableDashboards = [];
        if (has(formDashboards, 'ids')) {
            availableDashboards = isEmpty(formDashboards.ids)
                ? formData?.[GROUPS_ADMIN_PAGES_FIELD].ids || []
                : formDashboards.ids;
        } else {
            availableDashboards = isNil(formDashboards)
                ? formData?.[GROUPS_ADMIN_PAGES_FIELD].ids || []
                : formDashboards;
        }

        const availablePageMenuItems = Object.values(plmMainMenuPages)
            .map((page) => ({
                key: page.title,
                value: page.path,
            }))
            .filter((menuItem) => availablePages?.includes(menuItem.value) || false);
        const availableDashboardMenuItems = dashboards
            .map((dashboard) => {
                return {
                    key: dashboard.title,
                    value: dashboard._id,
                };
            })
            .filter((menuItem) => {
                return availableDashboards?.includes(menuItem.value) || false;
            });
        setMenuItems([NO_LANDING_PAGE_DEFAULT, ...availablePageMenuItems, ...availableDashboardMenuItems]);
    }, [plmMainMenuPages, formPages, formDashboards, dashboards, formData]);

    useEffect(() => {
        const availableDashboards = has(formDashboards, 'ids') ? formDashboards.ids : formDashboards;

        if (
            !isNil(formLandingPage) &&
            !formPages?.includes(formLandingPage) &&
            !availableDashboards?.includes(formLandingPage) &&
            formLandingPage !== NO_LANDING_PAGE_DEFAULT.value
        ) {
            setValue(GROUPS_ADMIN_LANDING_PAGE_FIELD, null);
        }
    }, [formLandingPage, formPages, formDashboards, setValue]);

    useEffect(() => {
        if (recordId === DEFAULT_GROUP_UUID) {
            setValue(GROUPS_ADMIN_LANDING_PAGE_FIELD, NO_LANDING_PAGE_DEFAULT.value);
        }

        if (isNil(formData.landingPage)) {
            setValue(GROUPS_ADMIN_LANDING_PAGE_FIELD, NO_LANDING_PAGE_DEFAULT.value);
        }
    }, [recordId, formData.landingPage, setValue]);

    return (
        <NgpSelect
            fieldName={GROUPS_ADMIN_LANDING_PAGE_FIELD}
            label={t`Group Landing Page`}
            className={styles.selectField}
            required
            disabled={disabled}
            helpTipText={t`Only pages and dashboards selected to be visible by this 
                group are available to be a landing page. If this selector is missing 
                an item you are looking for or it is empty, use the page and dashboard 
                pickers below to add visible pages and dashboards to the group.`}
        >
            {menuItems.map((menuItem) => (
                <MenuItem key={menuItem.key} value={menuItem.value}>
                    {menuItem.key}
                </MenuItem>
            ))}
        </NgpSelect>
    );
}
